import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress } from '@mui/material';
import { AuthProvider } from './contexts/AuthContext';

// Critical components - load immediately (used on almost every page)
import ProtectedRoute from './components/common/ProtectedRoute';
import RoleBasedDashboard from './components/common/RoleBasedDashboard';

// Lazy load all pages for code splitting
const LandingPage = lazy(() => import('./pages/LandingPage'));
const LoginPage = lazy(() => import('./pages/LoginPage'));
const PasswordResetPage = lazy(() => import('./pages/PasswordResetPage'));
const UnauthorizedPage = lazy(() => import('./pages/UnauthorizedPage'));
const ApiTestPage = lazy(() => import('./pages/ApiTestPage'));

// Designer pages
const DesignerPage = lazy(() => import('./pages/DesignerPage'));
const DesignerTestPage = lazy(() => import('./pages/DesignerTestPage'));
const TemplateDesignerFullPage = lazy(() => import('./pages/TemplateDesignerFullPage'));
const HybridDesignerTestPage = lazy(() => import('./pages/HybridDesignerTestPage'));
const FixedDesignerTestPage = lazy(() => import('./pages/FixedDesignerTestPage'));

// Main feature pages
const BulkOperationsPage = lazy(() => import('./pages/BulkOperationsPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const VerificationPage = lazy(() => import('./pages/VerificationPage'));
const SearchPage = lazy(() => import('./pages/SearchPage'));
const FileManagerPage = lazy(() => import('./pages/FileManagerPage'));

// Certificate pages
const CertificateListPage = lazy(() => import('./pages/CertificateListPage'));
const CertificateCreatePage = lazy(() => import('./pages/CertificateCreatePage'));
const CertificateDetailPage = lazy(() => import('./pages/CertificateDetailPage'));
const CertificateEditPage = lazy(() => import('./pages/CertificateEditPage'));
const CertificateGenerationPage = lazy(() => import('./pages/CertificateGenerationPage'));
const CertificateApproverPage = lazy(() => import('./pages/CertificateApproverPage'));
const StaffCertificatesPage = lazy(() => import('./pages/StaffCertificatesPage'));
const ActivityCertificatesPage = lazy(() => import('./pages/ActivityCertificatesPage'));
const ApproverCertificatesPage = lazy(() => import('./pages/ApproverCertificatesPage'));

// Approval pages
const ApprovalPage = lazy(() => import('./pages/ApprovalPage'));
const PendingApprovalsPage = lazy(() => import('./pages/PendingApprovalsPage'));

// User management pages
const UserListPage = lazy(() => import('./pages/UserListPage'));
const UserCreatePage = lazy(() => import('./pages/UserCreatePage'));
const UserDetailPage = lazy(() => import('./pages/UserDetailPage'));
const UserEditPage = lazy(() => import('./pages/UserEditPage'));
const UserProfilePage = lazy(() => import('./pages/UserProfilePage'));
const StudentManagementPage = lazy(() => import('./pages/StudentManagementPage'));

// Settings pages
const SystemSettingsPage = lazy(() => import('./pages/SystemSettingsPage'));
const NotificationSettingsPage = lazy(() => import('./pages/NotificationSettingsPage'));
const NotificationCenterPage = lazy(() => import('./pages/NotificationCenterPage'));
const SystemStatusPage = lazy(() => import('./pages/SystemStatusPage'));

// Template pages
const TemplateListPage = lazy(() => import('./pages/TemplateListPage'));
const TemplateCreatePage = lazy(() => import('./pages/TemplateCreatePage'));
const TemplateDetailPage = lazy(() => import('./pages/TemplateDetailPage'));
const TemplateDesignerPage = lazy(() => import('./pages/TemplateDesignerPage'));

// Activity pages
const ActivityListPage = lazy(() => import('./pages/ActivityListPage'));
const ActivityCreatePage = lazy(() => import('./pages/ActivityCreatePage'));
const ActivityDetailPage = lazy(() => import('./pages/ActivityDetailPage'));
const ActivityEditPage = lazy(() => import('./pages/ActivityEditPage'));

// Signatory & Verification pages
const SignatoryManagementPage = lazy(() => import('./pages/SignatoryManagementPage'));
const VerificationHistoryPage = lazy(() => import('./pages/VerificationHistoryPage'));
const PublicVerificationPage = lazy(() => import('./pages/PublicVerificationPage'));

// Signer pages
const SignerSigningPage = lazy(() => import('./pages/signer/SignerSigningPage'));
const SignerSignaturesPage = lazy(() => import('./pages/signer/SignerSignaturesPage'));
const SignerHistoryPage = lazy(() => import('./pages/signer/SignerHistoryPage'));
const SignerCompletedPage = lazy(() => import('./pages/signer/SignerCompletedPage'));
const SignerReturnedPage = lazy(() => import('./pages/signer/SignerReturnedPage'));
const SignerMonthlyReportPage = lazy(() => import('./pages/signer/SignerMonthlyReportPage'));
const SignerAnalyticsPage = lazy(() => import('./pages/signer/SignerAnalyticsPage'));
const PendingDocumentsPage = lazy(() => import('./pages/signer/PendingDocumentsPage'));

// Distribution & QR pages
const DistributionManagementPage = lazy(() => import('./pages/DistributionManagementPage'));
const QRCodeManagementPage = lazy(() => import('./pages/QRCodeManagementPage'));

// Audit & Student pages
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const MyCertificatesPage = lazy(() => import('./pages/student/MyCertificatesPage'));
const StudentActivitiesPage = lazy(() => import('./pages/student/StudentActivitiesPage'));

// Loading component with accessibility
const LoadingFallback = () => (
  <Box
    role="status"
    aria-live="polite"
    aria-label="กำลังโหลดเนื้อหา"
    sx={{
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      minHeight: '100vh',
      flexDirection: 'column',
      gap: 2,
    }}
  >
    <CircularProgress size={48} aria-hidden="true" />
    <Box sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
      กำลังโหลด...
    </Box>
  </Box>
);

// Create Material-UI theme inspired by Fund system
const theme = createTheme({
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#388e3c',
      light: '#66bb6a',
      dark: '#2e7d32',
    },
    background: {
      default: '#fafafa',
      paper: '#ffffff',
    },
    text: {
      primary: '#212121',
      secondary: '#757575',
    },
  },
  typography: {
    fontFamily: '"Sarabun", "Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    h1: {
      fontFamily: '"Sarabun", sans-serif',
      fontWeight: 700,
    },
    h2: {
      fontFamily: '"Sarabun", sans-serif',
      fontWeight: 700,
    },
    h3: {
      fontFamily: '"Sarabun", sans-serif',
      fontWeight: 600,
    },
    h4: {
      fontFamily: '"Sarabun", sans-serif',
      fontWeight: 600,
    },
    h5: {
      fontFamily: '"Sarabun", sans-serif',
      fontWeight: 600,
    },
    h6: {
      fontFamily: '"Sarabun", sans-serif',
      fontWeight: 600,
    },
    body1: {
      fontFamily: '"Sarabun", sans-serif',
    },
    body2: {
      fontFamily: '"Sarabun", sans-serif',
    },
    button: {
      fontFamily: '"Sarabun", sans-serif',
      fontWeight: 500,
    },
  },
  shape: {
    borderRadius: 12,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          fontWeight: 500,
          padding: '8px 24px',
        },
        contained: {
          boxShadow: '0 4px 12px rgba(25, 118, 210, 0.15)',
          '&:hover': {
            boxShadow: '0 6px 20px rgba(25, 118, 210, 0.25)',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 12,
        },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* Skip to main content for keyboard navigation */}
      <a href="#main-content" className="skip-to-main">
        ข้ามไปยังเนื้อหาหลัก
      </a>
      <AuthProvider>
        <Router>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/password-reset" element={<PasswordResetPage />} />
            <Route path="/unauthorized" element={<UnauthorizedPage />} />
            <Route path="/api-test" element={<ApiTestPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <RoleBasedDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/designer"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <DesignerPage />
                </ProtectedRoute>
              }
            />
            {/* Certificates - Role-based routing */}
            <Route
              path="/certificates"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <CertificateListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificates/staff"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <StaffCertificatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificates/approver"
              element={
                <ProtectedRoute allowedRoles={['Admin']}>
                  <CertificateApproverPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificates/create"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <CertificateCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificates/:id"
              element={
                <ProtectedRoute>
                  <CertificateDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/certificates/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin', 'signer']}>
                  <CertificateEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/approvals/pending"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin', 'super_admin']}>
                  <PendingApprovalsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/approvals/certificates"
              element={
                <ProtectedRoute allowedRoles={['admin', 'super_admin']}>
                  <ApproverCertificatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <TemplateListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/create"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <TemplateDesignerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/:id"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <TemplateDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/templates/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <TemplateDesignerPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/template-designer"
              element={
                <ProtectedRoute>
                  <TemplateDesignerFullPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/template-designer/:id"
              element={
                <ProtectedRoute>
                  <TemplateDesignerFullPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/hybrid-designer-test"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <HybridDesignerTestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/fixed-designer-test"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <FixedDesignerTestPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/approvals"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <ApprovalPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/create"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:id"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/users/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <UserEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/students"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <StudentManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <SystemSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <UserProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <ProtectedRoute>
                  <NotificationCenterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/system-status"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <SystemStatusPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bulk-operations"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <BulkOperationsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/analytics"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/search"
              element={
                <ProtectedRoute>
                  <SearchPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/files"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <FileManagerPage />
                </ProtectedRoute>
              }
            />
            {/* Activity Management routes */}
            <Route
              path="/activities"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <ActivityListPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities/create"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <ActivityCreatePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities/:id"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <ActivityDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities/:id/edit"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <ActivityEditPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activities/:activityId/certificates"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <ActivityCertificatesPage />
                </ProtectedRoute>
              }
            />

            {/* Certificate Generation routes */}
            <Route
              path="/certificates/generate"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <CertificateGenerationPage />
                </ProtectedRoute>
              }
            />

            {/* Distribution Management routes */}
            <Route
              path="/distribution"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <DistributionManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/distribution/email"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <DistributionManagementPage />
                </ProtectedRoute>
              }
            />

            {/* Signatory Management routes */}
            <Route
              path="/signatures"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <SignatoryManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signatories"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <SignatoryManagementPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signatures/history"
              element={
                <ProtectedRoute allowedRoles={['signer', 'admin']}>
                  <VerificationHistoryPage />
                </ProtectedRoute>
              }
            />

            {/* Verification & QR Management routes */}
            <Route
              path="/verification-history"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <VerificationHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/qr-management"
              element={
                <ProtectedRoute allowedRoles={['staff', 'admin']}>
                  <QRCodeManagementPage />
                </ProtectedRoute>
              }
            />

            {/* Audit Log routes */}
            <Route
              path="/audit-logs"
              element={
                <ProtectedRoute allowedRoles={['admin']}>
                  <AuditLogPage />
                </ProtectedRoute>
              }
            />

            {/* Download Center for students */}
            <Route
              path="/downloads"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <MyCertificatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/my-certificates"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <MyCertificatesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/student/activities"
              element={
                <ProtectedRoute allowedRoles={['student']}>
                  <StudentActivitiesPage />
                </ProtectedRoute>
              }
            />

            {/* Signer Routes */}
            <Route
              path="/signer/dashboard"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <RoleBasedDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/pending"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <PendingDocumentsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/sign"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <SignerSigningPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/sign/:id"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <SignerSigningPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/completed"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <SignerCompletedPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/returned"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <SignerReturnedPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/signatures"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <SignerSignaturesPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/history"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <SignerHistoryPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/analytics"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <SignerAnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/reports/monthly"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <SignerMonthlyReportPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/reports/performance"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <AnalyticsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/notifications"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <NotificationCenterPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/notifications/urgent"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <NotificationSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/notifications/settings"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <NotificationSettingsPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/profile"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <UserProfilePage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/signer/settings"
              element={
                <ProtectedRoute allowedRoles={['signer']}>
                  <SystemSettingsPage />
                </ProtectedRoute>
              }
            />

            {/* Public verification routes - no authentication required */}
            <Route path="/verify" element={<VerificationPage />} />
            <Route path="/verify/:code" element={<VerificationPage />} />
            <Route path="/public-verify" element={<PublicVerificationPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
